// -----------------------------------------------------------------------------
// core/store.ts — the single document state tree + subscription.
//
// Holds the whole editable document (project + imported assets + current
// selection) in one place. The UI reads snapshots via `state` and re-renders on
// `subscribe`; it changes the document only by `dispatch`-ing Commands (which go
// through undo/redo) or via the few explicitly non-undoable helpers (selection,
// asset registration, project load).
//
// The render engine reads this same state directly — it never mutates it.
// (CLAUDE.md §4: UI sends commands; engine reads state.)
// -----------------------------------------------------------------------------
import type { Command } from './commands';
import { History } from './history';
import type { ID, MediaAsset, Project } from './model';

export interface EditorState {
  project: Project;
  assets: MediaAsset[];
  selectedClipId: ID | null;
}

export class ProjectStore {
  /** Stable reference; its fields are mutated in place. Read freely; never mutate from the UI. */
  readonly state: EditorState;

  private listeners = new Set<() => void>();
  private history = new History();

  constructor(project: Project, assets: MediaAsset[] = []) {
    this.state = { project, assets, selectedClipId: null };
  }

  // ---- subscription ----------------------------------------------------------
  subscribe(fn: () => void): () => void {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  }
  /** Notify all listeners. Snapshot the set so a listener can unsubscribe mid-emit. */
  notify(): void {
    for (const fn of [...this.listeners]) fn();
  }

  // ---- undoable edits --------------------------------------------------------
  dispatch(cmd: Command): void {
    cmd.do(this.state);
    this.history.push(cmd);
    this.notify();
  }
  undo(): void {
    const cmd = this.history.undo();
    if (!cmd) return;
    cmd.undo(this.state);
    this.notify();
  }
  redo(): void {
    const cmd = this.history.redo();
    if (!cmd) return;
    cmd.do(this.state);
    this.notify();
  }
  canUndo(): boolean {
    return this.history.canUndo();
  }
  canRedo(): boolean {
    return this.history.canRedo();
  }

  // ---- non-undoable helpers --------------------------------------------------
  select(clipId: ID | null): void {
    if (this.state.selectedClipId === clipId) return;
    this.state.selectedClipId = clipId;
    this.notify();
  }
  addAsset(asset: MediaAsset): void {
    this.state.assets.push(asset);
    this.notify();
  }
  getAsset(id: ID): MediaAsset | undefined {
    return this.state.assets.find((a) => a.id === id);
  }

  /** Replace the entire document (e.g. after loading a saved project). */
  load(project: Project, assets: MediaAsset[]): void {
    this.state.project = project;
    this.state.assets = assets;
    this.state.selectedClipId = null;
    this.history.clear();
    this.notify();
  }
}
